import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Mail, Send, Archive, Inbox, SendHorizontal, Calendar } from 'lucide-react';
import { DatePickerFr } from '../ui/DatePickerFr';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Messagerie() {
  const { t } = useTranslation();
  const { user, getAuthHeader } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('received');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNewMessageOpen, setIsNewMessageOpen] = useState(false);
  
  const [newMessage, setNewMessage] = useState({
    destinataire_id: '',
    sujet: '',
    contenu: '',
    reference_solidaire: ''
  });

  const [filters, setFilters] = useState({
    reference_solidaire: '',
    date_debut: '',
    date_fin: ''
  });

  useEffect(() => {
    fetchMessages();
  }, [activeTab, filters]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('type_', activeTab);
      if (filters.reference_solidaire) params.append('reference_solidaire', filters.reference_solidaire);
      if (filters.date_debut) params.append('date_debut', filters.date_debut);
      if (filters.date_fin) params.append('date_fin', filters.date_fin);
      params.append('page', '1');
      params.append('page_size', '100');

      const response = await axios.get(`${API}/ged/messages?${params}`, {
        headers: getAuthHeader()
      });
      // Support ancien format (array) et nouveau format (avec pagination) - Trier du plus récent au plus ancien
      const msgs = response.data.data || response.data;
      const sortedMsgs = Array.isArray(msgs) ? msgs.sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0)) : msgs;
      setMessages(sortedMsgs);
    } catch {
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMessage = async (message) => {
    try {
      const response = await axios.get(`${API}/ged/messages/${message.id}`, {
        headers: getAuthHeader()
      });
      setSelectedMessage(response.data);
      setIsDialogOpen(true);
      fetchMessages(); // Rafraîchir pour mettre à jour le statut
    } catch {
      toast.error('Erreur lors de l\'ouverture du message');
    }
  };

  const handleArchiveMessage = async (messageId) => {
    try {
      await axios.put(`${API}/ged/messages/${messageId}/archive`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Message archivé');
      setIsDialogOpen(false);
      fetchMessages();
    } catch {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.destinataire_id || !newMessage.sujet || !newMessage.contenu) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await axios.post(`${API}/ged/messages`, newMessage, {
        headers: getAuthHeader()
      });
      toast.success('Message envoyé');
      setIsNewMessageOpen(false);
      setNewMessage({
        destinataire_id: '',
        sujet: '',
        contenu: '',
        reference_solidaire: ''
      });
      fetchMessages();
    } catch {
      toast.error('Erreur lors de l\'envoi du message');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (message) => {
    if (message.is_archived) {
      return <Badge className="bg-gray-600 text-white font-semibold">Archivé</Badge>;
    }
    if (message.statut === 'lu') {
      return <Badge className="bg-slate-600 text-white font-semibold">Lu</Badge>;
    }
    return <Badge className="bg-coppam-gold text-white font-semibold">Nouveau</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Bouton nouveau message */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">{t('messagerie.title', 'Messagerie Interne')}</h2>
        <Button
          onClick={() => setIsNewMessageOpen(true)}
          className="bg-coppam-gold hover:bg-coppam-gold/90"
        >
          <Send className="h-4 w-4 mr-2" />
          {t('messagerie.new_message', 'Nouveau message')}
        </Button>
      </div>

      {/* Filtres */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('forms.filter', 'Filtres')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>{t('messagerie.reference', 'Réf.Solidaire')}</Label>
              <Input
                value={filters.reference_solidaire}
                onChange={(e) => setFilters({ ...filters, reference_solidaire: e.target.value })}
                placeholder="Numéro..."
              />
            </div>
            <div>
              <Label>{t('forms.date_start', 'Date début')}</Label>
              <DatePickerFr
                className="w-full mt-1"
                value={filters.date_debut}
                onChange={(v) => setFilters({ ...filters, date_debut: v })}
              />
            </div>
            <div>
              <Label>{t('forms.date_end', 'Date fin')}</Label>
              <DatePickerFr
                className="w-full mt-1"
                value={filters.date_fin}
                onChange={(v) => setFilters({ ...filters, date_fin: v })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Messages */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Messages
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="received">
                <Inbox className="h-4 w-4 mr-2" />
                {t('messagerie.received', 'Reçus')}
              </TabsTrigger>
              <TabsTrigger value="sent">
                <SendHorizontal className="h-4 w-4 mr-2" />
                {t('messagerie.sent', 'Envoyés')}
              </TabsTrigger>
              <TabsTrigger value="all">{t('messagerie.all', 'Tous')}</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab}>
              {loading ? (
                <div className="flex justify-center py-4 md:py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coppam-gold"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8 text-slate-600">
                  {t('messagerie.no_messages', 'Aucun message')}
                </div>
              ) : (
                <div className="space-y-2">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      onClick={() => handleOpenMessage(message)}
                      className={`p-4 border rounded-lg cursor-pointer hover:bg-slate-50 transition ${
                        message.statut === 'envoye' && message.destinataire_id === user.id
                          ? 'bg-blue-50 border-blue-200'
                          : ''
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold">
                              {activeTab === 'sent'
                                ? `À: ${message.destinataire_nom}`
                                : `De: ${message.expediteur_nom}`}
                            </span>
                            {getStatusBadge(message)}
                            {message.reference_solidaire && (
                              <Badge className="bg-slate-600 text-white text-xs font-semibold">
                                Réf: {message.reference_solidaire}
                              </Badge>
                            )}
                          </div>
                          <h4 className="font-medium text-slate-800">{message.sujet}</h4>
                          <p className="text-sm text-slate-600 line-clamp-1">
                            {message.contenu}
                          </p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                            <Calendar className="h-3 w-3" />
                            {formatDate(message.date_envoi)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Dialog Lecture Message */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          {selectedMessage && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedMessage.sujet}</DialogTitle>
                <DialogDescription>
                  De: {selectedMessage.expediteur_nom} • {formatDate(selectedMessage.date_envoi)}
                  {selectedMessage.reference_solidaire && (
                    <span> • Réf: {selectedMessage.reference_solidaire}</span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="whitespace-pre-wrap text-slate-700">
                  {selectedMessage.contenu}
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => handleArchiveMessage(selectedMessage.id)}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Archiver
                </Button>
                <Button onClick={() => setIsDialogOpen(false)}>Fermer</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Nouveau Message */}
      <Dialog open={isNewMessageOpen} onOpenChange={setIsNewMessageOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau message</DialogTitle>
            <DialogDescription>Envoyer un message à un membre</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>ID Destinataire *</Label>
              <Input
                value={newMessage.destinataire_id}
                onChange={(e) => setNewMessage({ ...newMessage, destinataire_id: e.target.value })}
                placeholder="ID de l'utilisateur..."
              />
            </div>
            <div>
              <Label>Sujet *</Label>
              <Input
                value={newMessage.sujet}
                onChange={(e) => setNewMessage({ ...newMessage, sujet: e.target.value })}
                placeholder="Objet du message..."
              />
            </div>
            <div>
              <Label>Réf.Solidaire</Label>
              <Input
                value={newMessage.reference_solidaire}
                onChange={(e) => setNewMessage({ ...newMessage, reference_solidaire: e.target.value })}
                placeholder="Numéro de souscription..."
              />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea
                value={newMessage.contenu}
                onChange={(e) => setNewMessage({ ...newMessage, contenu: e.target.value })}
                placeholder="Votre message..."
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNewMessageOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSendMessage} className="bg-coppam-gold hover:bg-coppam-gold/90">
              <Send className="h-4 w-4 mr-2" />
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
